/** Owner-facing language (the owner app is in English and Spanish). */
export type Lang = 'en' | 'es';

/**
 * A worker's language: their page, instructions, and printed list are in it (Mike, 2026-10-15).
 * Spanish is the default. Polish comes next (phase 2); add it here once its wording exists.
 */
export const WORKER_LANGS = ['es', 'en'] as const;
export type WorkerLang = (typeof WORKER_LANGS)[number];

/** Each language named in itself, for language pickers. */
export const LANGUAGE_NAMES: Record<string, string> = { es: 'Español', en: 'English', pl: 'Polski' };

/** How the app addresses staff in Spanish: informal "tú" or formal "usted". Chosen per household. */
export type Formality = 'tu' | 'usted';

/** Text the owner wrote, plus its translation. In the prototype both sides are filled in by hand. */
export interface Bilingual {
  en: string;
  es: string;
  /** Spanish written for "usted", when it differs from the "tú" version. */
  esUsted?: string;
}

/**
 * Who a part of the house belongs to: the cleaning ("house") or the garden. Each has its own
 * people, areas, supplies, work days and hours, and daily report (Mike, 2026-10-14).
 */
export const CREWS = ['house', 'garden'] as const;
export type Crew = (typeof CREWS)[number];

/** Monday = 0 ... Sunday = 6. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const ROOM_ICONS = ['cooking-pot', 'bath', 'shower-head', 'toilet', 'bed-double', 'bed', 'sofa', 'utensils', 'tv', 'briefcase', 'washing-machine', 'door-open', 'trees', 'house', 'flower-2', 'tree-pine', 'waves', 'sprout', 'car'] as const;
export type RoomIcon = (typeof ROOM_ICONS)[number];

export interface Room {
  id: string;
  name: Bilingual;
  icon: RoomIcon;
  order: number;
}

export interface Task {
  id: string;
  roomId: string;
  name: Bilingual;
  minutes: number;
  instructions?: Bilingual;
  order: number;
  /** Days of the week this task is scheduled (the routine). */
  days: Weekday[];
}

export type ProductStatus = 'good' | 'low' | 'out';

export const PRODUCT_CATEGORIES = ['general', 'bathroom', 'kitchen', 'floors', 'disposables', 'other', 'garden'] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const PRODUCT_ICONS = ['milk', 'droplets', 'spray-can', 'trash-2', 'layers', 'square-stack', 'hand', 'sparkles', 'flask-conical', 'package', 'shirt', 'fuel', 'leaf', 'sprout'] as const;
export type ProductIcon = (typeof PRODUCT_ICONS)[number];

export interface Product {
  id: string;
  name: Bilingual;
  category: ProductCategory;
  icon: ProductIcon;
}

export type CouldNotReason = 'no_product' | 'no_time' | 'room_busy' | 'other';

export interface TaskResult {
  status: 'done' | 'could_not';
  /** When the button was pressed, as an ISO date-time. */
  at: string;
  /** Who marked it, for example "staff:rosa" or "owner:kate". */
  by: string;
  reason?: CouldNotReason;
  note?: string;
}

export interface ProductState {
  status: ProductStatus;
  at: string;
  by: string;
}
